import { useState } from "react";
import { FileIcon } from "./FileIcon";
import { onTablistKeyDown } from "./tablist";

interface TabsProps {
  files: { id: string; path: string }[];
  activeId: string | undefined;
  readOnly: boolean;
  onSelect: (id: string) => void;
  onMove: (id: string, index: number) => void;
}

const baseName = (path: string) => path.slice(path.lastIndexOf("/") + 1);

/**
 * One tab per file, in the workspace's shared order. Drag, or Alt+←/→, to reorder (for
 * everyone); arrow keys move between tabs.
 */
export function Tabs({ files, activeId, readOnly, onSelect, onMove }: TabsProps) {
  const [dragging, setDragging] = useState<string>();
  return (
    <div className="tabs-bar" role="tablist" aria-label="Open files" onKeyDown={onTablistKeyDown}>
      {files.map((file, index) => (
        <button
          key={file.id}
          type="button"
          role="tab"
          className="file-tab"
          aria-selected={file.id === activeId}
          tabIndex={file.id === activeId ? 0 : -1}
          onKeyDown={(e) => {
            if (readOnly || !e.altKey) return;
            const to = e.key === "ArrowLeft" ? index - 1 : e.key === "ArrowRight" ? index + 1 : -1;
            if (to < 0 || to >= files.length) return;
            e.preventDefault();
            onMove(file.id, to);
          }}
          title={file.path}
          draggable={!readOnly}
          onClick={() => onSelect(file.id)}
          onDragStart={(e) => {
            setDragging(file.id);
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", file.id);
          }}
          onDragEnd={() => setDragging(undefined)}
          onDragOver={(e) => {
            if (dragging) e.preventDefault();
          }}
          onDrop={(e) => {
            e.preventDefault();
            const id = e.dataTransfer.getData("text/plain") || dragging;
            if (id && id !== file.id) onMove(id, index);
            setDragging(undefined);
          }}
        >
          <FileIcon path={file.path} />
          {baseName(file.path)}
        </button>
      ))}
    </div>
  );
}
